import { Directive, effect, input } from '@angular/core';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { BaseNumberInputDirective } from './base-number-input.directive';
import {
  applyDigitPaste,
  canInsertDigit,
  isKeyboardNavigationOrEditKey,
  isPrintableDigitKey
} from './input-parse-format.helpers';

import type { ControlValueAccessor } from '@angular/forms';

/**
 * Rural Route or Po Box Input Directive
 *
 * Allows an input type number to have a max length of 4 for rural route and 5 for po box
 *
 * @example
 * <input type="number" [tdWbRuralRoutePoBoxInput]="RURAL_ROUTE" />
 */
@Directive({
  selector: 'input[tdWbRuralRoutePoBoxInput]',
  providers: [ValueAccessorProviderFactory(RuralRoutePoBoxInputDirective)],
  host: {
    '(input)': 'onInput($event)',
    '(keydown)': 'onKeyDown($event)',
    '(paste)': 'onPaste($event)',
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()'
  }
})
export class RuralRoutePoBoxInputDirective extends BaseNumberInputDirective implements ControlValueAccessor {

  private readonly PO_BOX_MAX_LENGTH: number = 5;
  private readonly RURAL_ROUTE_MAX_LENGTH: number = 4;
  readonly inputType = input<string | null | undefined>('RURAL_ROUTE', {
    alias: 'tdWbRuralRoutePoBoxInput'
  });

  constructor() {
    super('true');

    effect((): void => {
      this.inputType();
      super.onChanges();
    });
  }

  // Switch to keydown for better consistency and to handle IME/navigation keys correctly.
  // Allow only digits and enforce max length; allow deletion/navigation.

  onKeyDown(event: KeyboardEvent): void {
    const el = this.elementRef.nativeElement as HTMLInputElement;
    const key = event.key;

    // Allow control/meta/alt shortcuts and navigation/deletion keys
    if (event.ctrlKey || event.metaKey || event.altKey) { return; }
    if (isKeyboardNavigationOrEditKey(key)) {
      return; // never block these
    }

    // Only handle single-character (printable) keys from here
    if (key.length !== 1) { return; }

    // Digits only
    if (!isPrintableDigitKey(key)) {
      event.preventDefault();
      return;
    }

    const value = el.value ?? '';
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    if (!canInsertDigit(value, { start, end }, this.maxLength())) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    // Avoid base paste handler running and double-processing
    event.stopImmediatePropagation?.();
    event.stopPropagation();
    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const before = input.value ?? '';
    const start = input.selectionStart ?? before.length;
    const end = input.selectionEnd ?? before.length;

    const next = applyDigitPaste({
      currentValue: before,
      selection: { start, end },
      pastedText: event.clipboardData?.getData('text') ?? '',
      maxLength: this.maxLength()
    });
    this.renderer.setProperty(input, 'value', next);

    // Update internal state and model similar to the base input path
    const viewValue = next;
    let value: number;
    if (!this.isEmpty(viewValue)) {
      value = this.parseValue(viewValue);
    }
    this.updateModel(value);
    this.viewValue = viewValue;
  }

  protected formatValue(value: number): string {
    return value?.toString();
  }

  protected parseValue(value: string): number {
    return Number(value);
  }

  private maxLength(): number {
    return this.inputType() === 'PO_BOX' ? this.PO_BOX_MAX_LENGTH : this.RURAL_ROUTE_MAX_LENGTH;
  }
}
